import { useEffect, useRef } from 'react';
import {
  useAiDiscoveryExecutions,
  useAiDiscoverySummary,
} from '../../hooks/api/useAiDiscovery';
import { useCrawlerUnseenStore } from '../../store/crawlerUnseenStore';
import {
  resolveAiDiscoveryLiveCount,
  resolveIsAiDiscoveryRunning,
} from './ai-discovery-running.util';

export function useAiDiscoveryTriggerBadge({
  applicationId,
  enabled,
  isPanelOpen,
  isHistoryOpen,
}: {
  applicationId?: string;
  enabled: boolean;
  isPanelOpen: boolean;
  isHistoryOpen: boolean;
}) {
  const markTriggerUnseen = useCrawlerUnseenStore(
    (state) => state.markTriggerUnseen,
  );
  const markHistoryUnseen = useCrawlerUnseenStore(
    (state) => state.markHistoryUnseen,
  );
  const aiExecutionsQuery = useAiDiscoveryExecutions({
    applicationId: applicationId ?? '',
    enabled: !!applicationId && enabled,
  });
  const aiSummaryQuery = useAiDiscoverySummary({
    aiApplicationId: applicationId ?? '',
    enabled: !!applicationId && enabled,
  });
  const isAiDiscoveryRunning = resolveIsAiDiscoveryRunning({
    executions: aiExecutionsQuery.executions,
    discoverySummary: aiSummaryQuery.discoverySummary,
  });
  const aiLiveCount = resolveAiDiscoveryLiveCount({
    executions: aiExecutionsQuery.executions,
    discoverySummary: aiSummaryQuery.discoverySummary,
    isRunning: isAiDiscoveryRunning,
  });
  const wasRunningRef = useRef(false);
  const isPanelOpenRef = useRef(isPanelOpen);
  const isHistoryOpenRef = useRef(isHistoryOpen);

  useEffect(() => {
    isPanelOpenRef.current = isPanelOpen;
  }, [isPanelOpen]);

  useEffect(() => {
    isHistoryOpenRef.current = isHistoryOpen;
  }, [isHistoryOpen]);

  useEffect(() => {
    wasRunningRef.current = false;
  }, [applicationId]);

  useEffect(() => {
    if (!applicationId || !enabled) {
      wasRunningRef.current = false;

      return;
    }

    if (
      wasRunningRef.current &&
      !isAiDiscoveryRunning &&
      !isPanelOpenRef.current &&
      !isHistoryOpenRef.current
    ) {
      markTriggerUnseen(applicationId);
      markHistoryUnseen(applicationId);
    }

    wasRunningRef.current = isAiDiscoveryRunning;
  }, [
    applicationId,
    enabled,
    isAiDiscoveryRunning,
    markHistoryUnseen,
    markTriggerUnseen,
  ]);

  return {
    badgeCount: aiLiveCount,
    isLoading: aiSummaryQuery.isLoading,
  };
}
